import React from "react"
import { Container, Row, Col, Table, Button, Spinner } from "react-bootstrap"
import { AppPageWrapper } from "../NavMenu/AppPageWrapper"
import { Subscription, BehaviorSubject, of, concat } from "rxjs"
import { distinctUntilChanged, switchMap, map, catchError } from "rxjs/operators"
import { toast } from "react-toastify"
import { FaSync } from "react-icons/fa"
import FileSaver from "file-saver"
import SearchBar from "../SearchBar"
import SearchField from "../../api/SearchField"
import ColumnsCustomizeModal from "../ColumnsCustomizeModal/ColumnsCustomizeModal"
import { CustomizableFilterColumn } from "../ColumnsCustomizeModal/CustomizableColumn"
import ColumnHeaderFilter from "../ColumnHeaderFilter"
import TablePagination from "../TablePagination"
import FormatTimestampFileName from "../../formatters/FormatTimestampFileName"
import downloadCsv from "../../assets/images/download-csv.png"
import { SelectionInboxGridObservable, SelectionInboxGridCsv } from "../../api/Randoms/SelectionInboxGrid"
import SelectionInboxGridRow from "../../api/Randoms/SelectionInboxModel/SelectionInboxGridRow"
import { ColumnFilters } from "../../api/Randoms/SelectionInboxModel/ColumnFilters"
import { SearchFieldsGet } from "../../api/OrderResults/SearchFieldsGet"
import FormatSSN from "../../formatters/FormatSSN"
import { Link } from "react-router-dom"
import FormatDate from "../../formatters/FormatDate"
import EmployeeDetailsModal from "../OrderResults/EmployeeDetailsModal"
import { SelectionInboxGridResponse } from "../../api/Randoms/SelectionInboxModel/SelectionInboxGridResponse"
import { Filter, Sort } from "../../api/GridRequest"
import { StorageKey } from "../../config/Constants"
import { loadCustomized, saveCustomized } from "../ColumnsCustomizeModal/ColumnsCustomizeModal"
import ExcuseEventModal from "./ExcuseEventModal"
import UnexcuseEventModal from "./UnexcuseEventModal"
import UpdateStatusToPending from "../../api/Randoms/SelectionInboxPending"
import { isPortalAdmin } from "../../authentication/isAdminRole"
import { connect } from "react-redux"
import { AppState } from "../../AppState"
import GetDonorID, { GetDonorIDTitle } from "../../formatters/RenderDonorID"
import { GetSelectedPortal } from "../../authentication/SelectedCompany/CompanySelectionDrivenSelectors"

type Column = CustomizableFilterColumn<keyof SelectionInboxGridRow, keyof ColumnFilters>

interface SelectionInboxProps {
  isPortalAdmin: boolean
  unmaskDonorId: number
}

interface SelectionInboxState {
  loading: boolean
  data?: SelectionInboxGridResponse
  columns: Column[]
  loadingCsv?: boolean
  customizeModal: boolean
  searchFields?: SearchField[]
  details?: SelectionInboxGridRow
  excuseRow?: SelectionInboxGridRow
  unexcuseRow?: SelectionInboxGridRow
  employeeDetails: boolean
}

const defaultColumns: Column[] = [
  { name: "Status", filter: "Status", label: "STATUS", visible: true, customize: true, required: true },
  { name: "DonorID", filter: "IDNumber", label: "PERSONNEL ID", visible: true, customize: true },
  { name: "DonorName", filter: "Name", label: "NAME", visible: true, customize: true },
  { name: "Service", filter: "Service", label: "SERVICE", visible: true, customize: true },
  { name: "PoolName", filter: "Pool", label: "POOL", visible: true, customize: true },
  { name: "Selection", filter: "Selection", label: "SELECTION", visible: true, customize: true },
  { name: "CompanyName", filter: "Company", label: "COMPANY", visible: true, customize: true },
  { name: "LocationName", filter: "Location", label: "LOCATION", visible: false, customize: true },
  { name: "Regulated", filter: "Regulated", label: "REGULATED", visible: true, customize: true },
  { name: "EventDate", filter: "EventDate", label: "CREATED", visible: true, customize: true },
  { name: "DateNotified", filter: "NotificationDate", label: "NOTIFIED", visible: true, customize: true },
  { name: "FormFoxTestAN", filter: "AuthorizationID", label: "AUTHORIZATION ID", visible: true, customize: true },
  { name: "SiteName", filter: "Site", label: "SITE", visible: true, customize: true },
  { name: "RegulatoryMode", filter: "RegulatoryMode", label: "REGULATORY MODE", visible: true, customize: true },
  { name: "Occupation", filter: "Occupation", label: "OCCUPATION", visible: false, customize: true },
  { name: "LastName", filter: "LastName", label: "LAST NAME", visible: false, customize: true },
  { name: "MiddleInitial", filter: "MiddleInitial", label: "MIDDLE INITIAL", visible: false, customize: true },
  { name: "FirstName", filter: "FirstName", label: "FIRST NAME", visible: false, customize: true },
  { name: "Gender", filter: "Gender", label: "GENDER", visible: false, customize: true },
  { name: "DateOfBirth", filter: "DateOfBirth", label: "DOB", visible: false, customize: true },
  { name: "PrimaryIDType", filter: "PrimaryIDType", label: "PRIMARY ID TYPE", visible: false, customize: true },
  { name: "SSN", filter: "SSN", label: "SSN", visible: false, customize: true },
  { name: "DriverID", filter: "DriverID", label: "DRIVER ID", visible: false, customize: true },
  { name: "EmployerID", filter: "EmployerID", label: "EMPLOYER ID", visible: false, customize: true },
  { name: "AlternateID", filter: "AlternateID", label: "ALTERNATE ID", visible: false, customize: true },
  { name: "CRMUniqueID", filter: "CRMUniqueID", label: "CRM UNIQUE ID", visible: false, customize: true },
  { name: "HireDate", filter: "HireDate", label: "HIRE DATE", visible: false, customize: true },
  { name: "TerminationDate", filter: "TerminationDate", label: "TERMINATION DATE", visible: false, customize: true },
  { name: "CellPhone", filter: "CellPhone", label: "CELL PHONE", visible: false, customize: true },
  { name: "HomePhone", filter: "HomePhone", label: "HOME PHONE", visible: false, customize: true },
  { name: "WorkPhone", filter: "WorkPhone", label: "WORK PHONE", visible: false, customize: true },
  { name: "Email", filter: "Email", label: "EMAIL", visible: false, customize: true },
  { name: "Inactive", filter: "Inactive", label: "INACTIVE", visible: false, customize: true },
  { name: "RetireDate", filter: "RetireDate", label: "RETIRE DATE", visible: false, customize: true },
  { name: "Supervisor1", filter: "Supervisor1", label: "SUPERVISOR 1", visible: false, customize: true },
  { name: "Supervisor2", filter: "Supervisor2", label: "SUPERVISOR 2", visible: false, customize: true },
  { name: "Address", filter: "Address", label: "ADDRESS", visible: false, customize: true },
  { name: "Province", filter: "Province", label: "PROVINCE", visible: false, customize: true },
  { name: "Zip", filter: "Zip", label: "ZIP", visible: false, customize: true },
  { name: "Country", filter: "Country", label: "COUNTRY", visible: false, customize: true },
  { name: "StateCode", filter: "StateCode", label: "STATE CODE", visible: false, customize: true },
  { name: "StateName", filter: "StateName", label: "STATE NAME", visible: false, customize: true },
  { name: "Manager", filter: "Manager", label: "MANAGER", visible: false, customize: true },
]

interface SelectionInboxSearchParams {
  pageNumber: number
  sort: Sort
  filters: Filter[]
  search: { field: string, text: string }
}

const PageSize = 20
export class SelectionInbox extends React.PureComponent<SelectionInboxProps, SelectionInboxState> {
  searchFieldsSubscription?: Subscription
 
  state: SelectionInboxState = {
    loading: false,
    data: undefined,
    columns: loadCustomized(StorageKey.SelectionInboxGridColumns, defaultColumns) as Column[],
    customizeModal: false,
    employeeDetails: false
  }

  page$ = new BehaviorSubject<SelectionInboxSearchParams>({
    pageNumber: 1,
    sort: { column: "", order: "" },
    filters: [],
    search: { field: "", text: "" }
  })
  pageSubscription?: Subscription
  csvSubscription?: Subscription

  componentDidMount() {
    this.pageSubscription = this.page$.pipe(
      distinctUntilChanged(),
      switchMap(params => concat(
        of({ loading: true }),
        SelectionInboxGridObservable(params.pageNumber, PageSize, params.sort, params.filters, params.search).pipe(
          map((result: SelectionInboxGridResponse) => ({ data: result, loading: false })),
          catchError((error) => of({ loading: false }))
        )
      ))
    ).subscribe(
      (newState: { loading: boolean, data?: SelectionInboxGridResponse }) => {
        if (newState.data) {
          // Loads prev page in case of current non-first page is empty
          if ((!newState.data.Records || newState.data.Records.length === 0) && this.page$.value.pageNumber > 1) {
            this.page$.next({ ...this.page$.value, pageNumber: this.page$.value.pageNumber - 1 })
          } else {
            if (newState.data.Regulated?.length) {
              newState.data.Regulated = newState.data.Regulated.map(regulated => regulated ? "True" : "False")
            }

            if (this.props.isPortalAdmin && newState.data.Manager?.length) {
              newState.data.Manager = newState.data.Manager.map(manager => manager ? manager = "TRUE" : manager = "FALSE")
              newState.data.Records = this.mapManager(newState.data.Records)
            }
            else {
              this.setState({columns: this.state.columns.filter( x => x.name != "Manager")})
            }

            this.setState({ loading: newState.loading, data: newState.data })
            
          }
        } else {
          this.setState({ loading: newState.loading })
        }

      },
      (error: string) => {
        this.setState({ loading: false })
        toast.error(error)
      }
    )

    this.searchFieldsSubscription = SearchFieldsGet().subscribe(
      (data: SearchField[]) => this.setState({ searchFields: data }),
      error => toast.error(error)
    )
  }

  mapManager = (records: SelectionInboxGridRow[][]): SelectionInboxGridRow[][] => {
    return records.map((r) => {
      return r.map((element) => {
        element.Manager = (element.Manager || element.Manager == "TRUE") ? "TRUE" : "FALSE"
        return element
      })
    })
  }

  componentWillUnmount() {
    this.pageSubscription && this.pageSubscription.unsubscribe()
    this.csvSubscription && this.csvSubscription.unsubscribe()
    this.searchFieldsSubscription && this.searchFieldsSubscription.unsubscribe()
    this.page$.unsubscribe()
  }

  pageChanged = (pageNumber: number) => {
    this.page$.next({ ...this.page$.value, pageNumber: pageNumber })
  }

  sortGrid(column: string, order?: string) {
    this.page$.next({ ...this.page$.value, sort: { column: column, order: order || "" } })
  }

  filterGrid(column: string, items: string[]) {
    const filters = [...this.page$.value.filters]
    const filter = { column: column, values: items }
    let index = filters.findIndex(item => item.column === filter.column)
    if (index >= 0) {
      filters[index] = filter
    } else {
      filters.push(filter)
    }

    this.page$.next({ ...this.page$.value, pageNumber: 1, filters: filters })
  }

  clearFilters() {
    this.page$.next({ ...this.page$.value, pageNumber: 1, filters: [] })
  }

  onSearch = (field: string, text: string) => {
    this.page$.next({ ...this.page$.value, pageNumber: 1, search: { field: field, text: text } })
  }

  onExportClick = () => {
    this.setState({ loadingCsv: true })
    const value = this.page$.value
    this.csvSubscription = SelectionInboxGridCsv(value.pageNumber, 0, value.sort, value.filters, value.search).subscribe(
      result => this.export(result),
      error => {
        this.setState({ loadingCsv: false })
        toast.error(error)
      },
      (() => this.setState({ loadingCsv: false }))
    )
  }

  export(csv: string) {
    const csvData = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    FileSaver.saveAs(csvData, FormatTimestampFileName("Selection_Inbox", "csv", new Date()), true)
    toast.success("CSV file downloaded successfully.")
  }

  print(base64pdf: string) {
    const pdfWindow = window.open("")
    if (pdfWindow) {
      pdfWindow.document.write("<iframe width='100%' height='100%' src='data:application/pdf;base64, " + encodeURI(base64pdf) + "'></iframe>")
    }
  }

  handleExcuseModalClose = (success: boolean) => {
    this.setState({ excuseRow: undefined })
    if (success) {
      this.refreshGrid()
    }
  }

  handleUnexcuseModalClose = (success: boolean) => {
    this.setState({ unexcuseRow: undefined })
    if (success) {
      this.page$.next({ ...this.page$.value })
    }
  }

  isCanExcuse = (row: SelectionInboxGridRow)=> {
    return row.Status.startsWith("PENDING")
  }

  isCanUnexcuse = (row: SelectionInboxGridRow)=> {
    return row.Status.startsWith("EXCUSED")
  }

  visibleColumns = () => this.state.columns.filter(col => col.visible)

  statusButton = (row: SelectionInboxGridRow, group: SelectionInboxGridRow[]) => {
    switch(row.Status) {
      case "ORDERED":
      case "ORDERED ALT":{
          return <Button variant="link" className="p-0 btn-icon-link" onClick={() => this.setState({ details: row, employeeDetails: true })}>{row.Status}</Button>
      }
      case "EXCUSED":{
          return <Button variant="link" className="p-0 btn-icon-link" onClick={() => this.setState({ unexcuseRow: row })}>{row.Status}</Button>
      }
      default:{
          return <Link className="btn-link btn-icon-link" to={{ pathname: "/order", state: { formData: group } }}>{row.Status}</Link>
      }
    }
  }

  setPending = (row: SelectionInboxGridRow, group: SelectionInboxGridRow[]) => {
    if (["ORDERED", "ORDERED ALT"].includes(row.Status))
      return <Button
       variant="link"
       className="text-left p-0 btn-icon-link"
       onClick={() => this.updateToPending(group)}>
        [Set to PENDING]
       </Button>
    else
      return ""
  }

  updateToPending = (group: SelectionInboxGridRow[]) => {
    UpdateStatusToPending(group).subscribe(
      result => this.refreshGrid(),
      error => toast.error(error)
    )
  }

  createdDateButton = (row: SelectionInboxGridRow) => {
    if (this.isCanExcuse(row)) {
      return <Button variant="link" className="p-0 btn-icon-link" onClick={() => this.setState({ excuseRow: row })}>{FormatDate(row["EventDate"])}</Button>
    }
    else if (this.isCanUnexcuse(row)) {
      return <Button variant="link" className="p-0 btn-icon-link" onClick={() => this.setState({ unexcuseRow: row })}>{FormatDate(row["EventDate"])}</Button>
    }
    else {
       return FormatDate(row["EventDate"])
    }
  }

  renderCell = (row: SelectionInboxGridRow, colName: keyof SelectionInboxGridRow, key: number, isFirstRow: boolean, isLastRow: boolean, group: SelectionInboxGridRow[]) => {
    switch (colName) {
      case "DonorID":
        const idValue = GetDonorID(row[colName], row["ID"], this.props.unmaskDonorId)
        return <td key={key} className={`align-text-top group-cell ${isFirstRow ? "first" : ""} ${isLastRow ? "last" : ""}`}>{isFirstRow && idValue}</td>
      case "DonorName":
        return <td key={key} className={`align-text-top group-cell ${isFirstRow ? "first" : ""} ${isLastRow ? "last" : ""}`}>{isFirstRow && row[colName]}</td>
      case "Status":
        return <td key={key} className={`align-text-top group-cell ${isFirstRow ? "first" : ""} ${isLastRow ? "last" : ""}`}>
          {isFirstRow && this.statusButton(row, group)}
          {isFirstRow && this.setPending(row, group)}
        </td>
      case "EventDate":
        return <td key={key} className="align-text-top">{this.createdDateButton(row)}</td>
      case "DateNotified":
        return <td key={key} className="align-text-top">{FormatDate(row[colName])}</td>
      default:
        return <td key={key} className="align-text-top">{row[colName]}</td>
    }
  }

  headerColumns = () => this.visibleColumns().map((col: Column, i) => {
    const sort = this.page$.value.sort
    return (
      <th key={col.name}>
        {this.state.data && col.filter && col.filter in this.state.data && <ColumnHeaderFilter
          title={col.label || col.name}
          filteredItems={this.page$.value.filters.find(item => item.column === col.filter)?.values || []}
          sort={sort.column === col.name ? sort.order === "asc" ? "asc" : "desc" : undefined}
          items={this.state.data[col.filter as keyof ColumnFilters]?.filter(item => !!item) || []}
          onSort={(order?: string) => this.sortGrid(col.name, order)}
          onFilter={(items: string[]) => col.filter && this.filterGrid(col.filter, items)}
        />}
        {(!this.state.data || !col.filter) && (col.label || col.name)}
      </th>
    )
  })

  formatSearchString = (ID: string, value: string): string => {
    return ID === "SSN" ? FormatSSN(value) : value
  }
  
  refreshGrid = () => this.page$.next({ ...this.page$.value })

  render() {
    const emptyRow = <tr><td colSpan={this.visibleColumns().length}>NO DATA FOUND</td></tr>
    const groupedRows = (data: SelectionInboxGridResponse) => data.Records.map((rows: SelectionInboxGridRow[]) => dataRows(rows))
    const dataRows = (rows: SelectionInboxGridRow[]) => rows.map((row: SelectionInboxGridRow, i: number) => <tr key={i}>{dataColumns(row, i === 0, i === rows.length - 1, rows)}</tr>)
    const dataColumns = (row: SelectionInboxGridRow, isFirstRow: boolean, isLastRow: boolean, group: SelectionInboxGridRow[]) => this.visibleColumns().map((col, i) => this.renderCell(row, col.name as keyof SelectionInboxGridRow, i, isFirstRow, isLastRow, group))

    return <>
      <AppPageWrapper parentClassName={"align-items-center"} showNav>
        {this.state.employeeDetails && this.state.details && <EmployeeDetailsModal
          personID={this.state.details.PersonID}
          name={this.state.details.DonorName}
          id={this.state.details.DonorID}
          onHide={(updated) => this.setState({ employeeDetails: false }, () => updated && this.refreshGrid())} />}
        <Container fluid className="grid-container">
          <Row className="pb-2">
            <Col>
              <SearchBar onSearch={this.onSearch} searchFields={this.state.searchFields || []} formatValue={this.formatSearchString} allowBlank placeholder="Select" />
            </Col>
            <Col lg={6} className={"text-right"}>
              <Button variant="secondary" size="sm" onClick={() => this.setState({ customizeModal: true })}>CUSTOMIZE</Button>
              <Button variant="icon-link" size="sm" onClick={() => this.onExportClick()} className="text-dark">
                EXPORT CSV&nbsp;
                {this.state.loadingCsv ? <Spinner animation="border" size="sm" /> : <img src={downloadCsv} alt="" width="16" height="16" />}
              </Button>
              <Button variant="icon-link" size="sm" onClick={() => this.clearFilters()} className="text-dark">CLEAR FILTERS<FaSync /></Button>
              <ColumnsCustomizeModal show={this.state.customizeModal}
                columns= {this.props.isPortalAdmin ? this.state.columns : this.state.columns.filter( x => x.name != "Manager")}
                storeKey={StorageKey.SelectionInboxGridColumns}
                onClose={() => this.setState({ customizeModal: false })}
                onSave={columns => this.setState({ columns: columns as Column[], customizeModal: false })} />
            </Col>
          </Row>
          <Row>
            <Col>
              <Table bordered responsive>
                <thead>
                  <tr>
                    {this.headerColumns()}
                  </tr>
                </thead>
                <tbody>
                  {
                    this.state.loading ?
                      <tr><td colSpan={this.visibleColumns().length + 1}><Spinner animation="border" variant="secondary" /></td></tr> :
                      this.state.data?.Records && this.state.data.Records.length > 0 ? groupedRows(this.state.data) : emptyRow
                  }
                </tbody>
              </Table>
              {this.state.data && !this.state.loading &&
                <span className="total-records">Total records: {this.state.data.Total}</span>}
              {this.state.data && !this.state.loading &&
                <TablePagination pageNumber={this.page$.value.pageNumber} pages={this.state.data.Pages} onChange={this.pageChanged} />}
            </Col>
          </Row>
        </Container>
      </AppPageWrapper>
      {!!this.state.excuseRow && <ExcuseEventModal
        show={!!this.state.excuseRow}
        excuseDetails={this.state.excuseRow}
        onClose={this.handleExcuseModalClose} />}
      {!!this.state.unexcuseRow && <UnexcuseEventModal
        show={!!this.state.unexcuseRow}
        excuseDetails={this.state.unexcuseRow}
        onClose={this.handleUnexcuseModalClose} />}
    </>
  }
}

function mapStateToProps(state: AppState): SelectionInboxProps {
  const portal = GetSelectedPortal(state)
  const unmaskDonorId = portal ? portal.UnmaskDonorID : 0
  return {
    isPortalAdmin: isPortalAdmin(state),
    unmaskDonorId: unmaskDonorId
  }
}
const ConnectedSelectionInbox = connect(mapStateToProps)(SelectionInbox)
export default ConnectedSelectionInbox
